import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { analyticsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Eye, Download, MessageCircle, TrendingUp, Copy, Share2, ExternalLink, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatBadge = ({ icon: Icon, value, label, color, delay }) => (
  <motion.div
    className="flex flex-col items-center gap-1"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
  >
    <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-1"
      style={{ background: `${color}22`, border: `1px solid ${color}40` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <span className="text-2xl font-bold text-white leading-none">{(value || 0).toLocaleString()}</span>
    <span className="text-xs text-slate-400 text-center leading-tight">{label}</span>
  </motion.div>
);

export default function ShareSnapshotPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/share/${slug}`;
  const profileUrl = `${window.location.origin}/p/${slug}`;

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await analyticsAPI.getSnapshot(slug);
        setData(resp.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    if (!data) return;
    const text = `Check out ${data.profile.displayName}'s MeTag profile — ${(data.stats.totalViews || 0).toLocaleString()} profile views & counting!`;
    if (navigator.share) {
      navigator.share({ title: `${data.profile.displayName} on MeTag`, text, url: profileUrl }).catch(() => {});
    } else {
      handleCopy();
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center text-center p-8">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Snapshot Not Found</h2>
        <p className="text-slate-400">This profile hasn't set up their analytics snapshot yet.</p>
        <Link to="/" className="text-violet-400 hover:text-violet-300 mt-4 inline-block">← Go Home</Link>
      </div>
    </div>
  );

  const { profile, stats } = data;
  const profilePic = profile.profilePicture;
  const coverPhoto = profile.coverPhoto;

  return (
    <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center p-4 sm:p-8"
      data-testid="share-snapshot-page">

      {/* Card — screenshot-optimised */}
      <motion.div
        className="w-full max-w-sm relative overflow-hidden rounded-3xl shadow-2xl"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        data-testid="snapshot-card"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e0f3c 50%, #0f172a 100%)' }}
      >
        {/* Ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7c3aed, transparent)' }} />

        {/* Cover photo strip or gradient header */}
        <div className="h-28 relative overflow-hidden">
          {coverPhoto ? (
            <img src={coverPhoto} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="h-full" style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7, #ec4899)' }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0f172a]/90" />
        </div>

        {/* Avatar overlapping the cover */}
        <div className="flex flex-col items-center -mt-14 px-6 pb-6">
          <div className="w-24 h-24 rounded-full border-4 border-[#0f172a] overflow-hidden bg-slate-800 shadow-xl mb-3">
            {profilePic ? (
              <img src={profilePic} alt={profile.displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                <span className="text-3xl font-bold text-white">{(profile.displayName || slug)[0]?.toUpperCase()}</span>
              </div>
            )}
          </div>

          <h1 className="text-xl font-bold text-white mb-0.5 text-center">{profile.displayName || slug}</h1>
          <p className="text-violet-400 text-sm mb-1">@{slug}</p>
          {profile.shortBio && (
            <p className="text-slate-400 text-xs text-center mb-4 max-w-xs leading-relaxed">{profile.shortBio}</p>
          )}

          {/* Divider */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent mb-5" />

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4 w-full mb-5">
            <StatBadge icon={Eye} value={stats.totalViews} label="Profile Views" color="#7c3aed" delay={0.1} />
            <StatBadge icon={Download} value={stats.vcardDownloads} label="Contacts Saved" color="#10b981" delay={0.2} />
            <StatBadge icon={MessageCircle} value={stats.whatsappClicks} label="WhatsApp Taps" color="#22c55e" delay={0.3} />
            <StatBadge icon={TrendingUp} value={stats.totalActions} label="Total Actions" color="#a855f7" delay={0.4} />
          </div>

          {/* CTA button */}
          <motion.a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm text-white mb-3 transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            data-testid="view-profile-btn"
          >
            <ExternalLink className="w-4 h-4" />
            View My MeTag Profile
          </motion.a>

          {/* Branding */}
          <div className="flex items-center gap-1.5 text-slate-600 text-xs">
            <CreditCard className="w-3.5 h-3.5" />
            <span>Powered by MeTag.cc · NFC Smart Cards</span>
          </div>
        </div>
      </motion.div>

      {/* Share actions below the card */}
      <motion.div
        className="mt-6 flex flex-col items-center gap-3 w-full max-w-sm"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <p className="text-slate-500 text-sm text-center">Share your stats on Instagram, LinkedIn, or WhatsApp</p>
        <div className="flex gap-3 w-full">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-medium transition-all"
            style={{ background: '#1e293b', color: copied ? '#10b981' : '#94a3b8', border: '1px solid #334155' }}
            data-testid="copy-snapshot-link-btn"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={handleNativeShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            data-testid="share-snapshot-btn"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>

        <Link to={`/p/${slug}`} className="text-slate-600 hover:text-slate-400 text-xs mt-1">
          ← Back to profile
        </Link>
      </motion.div>
    </div>
  );
}
